// Central calculation engine for the CASS Business Numbers Calculator.
// Formulas live here (protected); explanatory copy lives in the admin-editable config.

export const parseNum = (x) => {
  if (x === "" || x === null || x === undefined) return null;
  const n = parseFloat(String(x).replace(/[£,\s%]/g, ""));
  return Number.isNaN(n) ? null : n;
};

const has = (v) => v !== null && v !== undefined && !Number.isNaN(v);

// Period → number of days / months represented by the entered figures.
export const periodMeta = (period, customDays) => {
  if (period === "custom") {
    const d = has(parseNum(customDays)) ? parseNum(customDays) : 365;
    return { days: d, months: d / 30.4375 };
  }
  return { days: 365, months: 12 };
};

// Metric definitions: key, section, display format, whether it's an advanced/optional metric,
// the raw inputs required, and the formula. `requiresAny` = at least one of the listed inputs.
export const METRICS = [
  // Section 1 — Profitability
  { key: "gross_profit", section: "profitability", format: "currency", requires: ["turnover", "direct_costs"],
    fn: (v) => v.turnover - v.direct_costs },
  { key: "gross_margin", section: "profitability", format: "percent", requires: ["turnover", "direct_costs"],
    fn: (v) => (v.turnover !== 0 ? ((v.turnover - v.direct_costs) / v.turnover) * 100 : null) },
  { key: "net_margin", section: "profitability", format: "percent", requires: ["turnover", "net_profit"],
    fn: (v) => (v.turnover !== 0 ? (v.net_profit / v.turnover) * 100 : null) },
  { key: "breakeven_annual", section: "profitability", format: "currency", requires: ["turnover", "direct_costs", "total_overheads"],
    fn: (v) => { const gm = v.turnover !== 0 ? (v.turnover - v.direct_costs) / v.turnover : null; return gm && gm > 0 ? v.total_overheads / gm : null; },
    naNote: "This needs a positive gross margin to calculate." },
  { key: "breakeven_monthly", section: "profitability", format: "currency", requires: ["turnover", "direct_costs", "total_overheads"],
    fn: (v, m) => { const gm = v.turnover !== 0 ? (v.turnover - v.direct_costs) / v.turnover : null; return gm && gm > 0 ? (v.total_overheads / gm) / m.months : null; },
    naNote: "This needs a positive gross margin to calculate." },
  { key: "overhead_pct", section: "profitability", format: "percent", optional: true, requires: ["turnover", "total_overheads"],
    fn: (v) => (v.turnover !== 0 ? (v.total_overheads / v.turnover) * 100 : null) },
  { key: "payroll_pct", section: "profitability", format: "percent", optional: true, requires: ["turnover"], requiresAny: ["direct_payroll", "overhead_payroll"],
    fn: (v) => (v.turnover !== 0 ? (((v.direct_payroll || 0) + (v.overhead_payroll || 0)) / v.turnover) * 100 : null) },

  // Section 2 — Cash & working capital
  { key: "cash_available", section: "cash", format: "currency", requires: ["cash_in_bank"], fn: (v) => v.cash_in_bank },
  { key: "cash_runway", section: "cash", format: "months", requires: ["cash_in_bank"], special: "cash_runway" },
  { key: "debtor_days", section: "cash", format: "days", requires: ["trade_debtors", "turnover"],
    fn: (v, m) => (v.turnover !== 0 ? (v.trade_debtors / v.turnover) * m.days : null) },
  { key: "creditor_days", section: "cash", format: "days", optional: true, requires: ["trade_creditors", "direct_costs"],
    fn: (v, m) => (v.direct_costs !== 0 ? (v.trade_creditors / v.direct_costs) * m.days : null),
    assumption: "Estimated using cost of sales as a proxy for purchases." },
  { key: "working_capital", section: "cash", format: "currency", requires: ["current_assets", "current_liabilities"],
    fn: (v) => v.current_assets - v.current_liabilities },
  { key: "current_ratio", section: "cash", format: "ratio", requires: ["current_assets", "current_liabilities"],
    fn: (v) => (v.current_liabilities !== 0 ? v.current_assets / v.current_liabilities : null) },
  { key: "mrc_pct", section: "cash", format: "percent", optional: true, requires: ["monthly_recurring_costs", "turnover"],
    fn: (v, m) => { const monthlyRev = v.turnover / m.months; return monthlyRev !== 0 ? (v.monthly_recurring_costs / monthlyRev) * 100 : null; } },

  // Section 3 — Customers & revenue
  { key: "avg_customer_value", section: "customers", format: "currency", requires: ["turnover", "num_customers"],
    fn: (v) => (v.num_customers !== 0 ? v.turnover / v.num_customers : null) },
  { key: "customer_concentration", section: "customers", format: "percent", requires: ["largest_customer_revenue", "turnover"],
    fn: (v) => (v.turnover !== 0 ? (v.largest_customer_revenue / v.turnover) * 100 : null) },
  { key: "recurring_revenue_pct", section: "customers", format: "percent", optional: true, requires: ["recurring_revenue", "turnover"],
    fn: (v) => (v.turnover !== 0 ? (v.recurring_revenue / v.turnover) * 100 : null) },
  { key: "revenue_growth", section: "customers", format: "percent", optional: true, requires: ["turnover", "prev_revenue"],
    fn: (v) => (v.prev_revenue > 0 ? ((v.turnover - v.prev_revenue) / v.prev_revenue) * 100 : null),
    naNote: "This needs a positive previous-period revenue figure." },

  // Section 4 — Business performance
  { key: "revenue_per_employee", section: "performance", format: "currency", requires: ["turnover", "num_employees"],
    fn: (v) => (v.num_employees !== 0 ? v.turnover / v.num_employees : null) },
  { key: "profit_per_employee", section: "performance", format: "currency", requires: ["net_profit", "num_employees"],
    fn: (v) => (v.num_employees !== 0 ? v.net_profit / v.num_employees : null) },
  { key: "profit_growth", section: "performance", format: "percent", optional: true, requires: ["net_profit", "prev_profit"],
    fn: (v) => (v.prev_profit > 0 ? ((v.net_profit - v.prev_profit) / v.prev_profit) * 100 : null),
    naNote: "This needs a positive previous-period profit figure." },
];

const monthlyOperatingCost = (v, m) => {
  if (has(v.direct_costs) && has(v.total_overheads)) return (v.direct_costs + v.total_overheads) / m.months;
  if (has(v.turnover) && has(v.net_profit)) return (v.turnover - v.net_profit) / m.months;
  return null;
};

// Compute every metric from the raw (string) input map.
export const computeMetrics = (rawInputs, period, customDays) => {
  const v = {};
  Object.keys(rawInputs || {}).forEach((k) => { v[k] = parseNum(rawInputs[k]); });
  const m = periodMeta(period, customDays);
  const out = {};

  METRICS.forEach((def) => {
    const missing = (def.requires || []).filter((k) => !has(v[k]));
    if ((def.requiresAny || []).length && !def.requiresAny.some((k) => has(v[k]))) missing.push(def.requiresAny.join("|"));

    if (missing.length) { out[def.key] = { status: "missing", value: null, missing }; return; }

    let value = null;
    if (def.special === "cash_runway") {
      const cost = monthlyOperatingCost(v, m);
      if (!has(cost)) { out[def.key] = { status: "missing", value: null, missing: ["direct_costs|total_overheads"] }; return; }
      if (cost <= 0) { out[def.key] = { status: "na", value: null, note: "Your costs need to be above zero to estimate a runway." }; return; }
      value = v.cash_in_bank / cost;
    } else {
      value = def.fn(v, m);
    }

    if (!has(value) || !Number.isFinite(value)) {
      out[def.key] = { status: "na", value: null, note: def.naNote || "This can't be calculated from the figures entered." };
      return;
    }
    out[def.key] = { status: "ok", value, assumption: def.assumption };
  });

  // Break-even headroom (derived)
  const be = out.breakeven_annual;
  if (be && be.status === "ok" && has(v.turnover)) {
    const amount = v.turnover - be.value;
    out._headroom = { status: "ok", amount, pct: be.value !== 0 ? (amount / be.value) * 100 : null };
  } else {
    out._headroom = { status: "missing" };
  }
  out._period = m;
  out._inputs = v;
  return out;
};

// ---- Formatting ----
export const fmtCurrency = (n) => {
  const r = Math.round(n);
  return (r < 0 ? "-£" : "£") + Math.abs(r).toLocaleString("en-GB");
};
export const fmtValue = (format, n) => {
  if (!has(n) || !Number.isFinite(n)) return "—";
  switch (format) {
    case "currency": return fmtCurrency(n);
    case "percent": return `${n.toFixed(1)}%`;
    case "days": return `${Math.round(n)} days`;
    case "months": return `${n.toFixed(1)} months`;
    case "ratio": return n.toFixed(2);
    default: return Math.round(n).toLocaleString("en-GB");
  }
};

// ---- Three numbers worth attention (rule-based, non-judgmental) ----
const ATTENTION_RULES = [
  { key: "cash_runway", metric: "cash_runway", test: (x) => x < 3 },
  { key: "current_ratio", metric: "current_ratio", test: (x) => x < 1 },
  { key: "breakeven", metric: null, test: (r) => r._headroom?.status === "ok" && r._headroom.amount < 0 },
  { key: "debtor_days", metric: "debtor_days", test: (x) => x > 45 },
  { key: "customer_concentration", metric: "customer_concentration", test: (x) => x > 25 },
  { key: "net_margin", metric: "net_margin", test: (x) => x < 5 },
];

export const selectAttention = (results) => {
  const picked = [];
  for (const rule of ATTENTION_RULES) {
    if (picked.length >= 3) break;
    if (rule.metric === null) {
      if (rule.test(results)) picked.push({ key: rule.key });
    } else {
      const r = results[rule.metric];
      if (r && r.status === "ok" && rule.test(r.value)) picked.push({ key: rule.key, metric: rule.metric, value: r.value });
    }
  }
  return picked;
};
